import * as React from 'react';
import { useState } from 'react';
import { logIn } from '../../../../api/Auth';
import {
    resendRegistrationText,
    redeemPhoneCode,
    SetPasswordDto
} from '../../../../api/ProspectUser';
import { ApiResult } from '../../../../api/Shared';
import { browserHistory } from '../../../../utilities/history';
import { setTokens } from '../../../../utilities/identity';
import { formatPhoneNumber } from '../../../../utilities/Utils';
import Button from '../../../common/form/Button';
import InputWithValidationIcon from '../../../common/form/InputWithValidationIcon';
import { prospectPath, prospectPages } from '../ProspectRouter';
import { TourPage, tourSignupPath } from '../signup/TourRouter';
import ProspectNewPasswordEntry from './ProspectNewPasswordEntry';

interface Props {
    phoneNumber: string;
    onPasswordSubmit: (passwordDto: SetPasswordDto) => Promise<ApiResult<boolean>>;
    changePhoneNumberEvent: () => void;
    setIsLoading: (isLoading: boolean) => void;
    setErrorMessage: (error: string | undefined) => void;
    verificationCodeInputLabel: string;
    codeConfirmedLabel?: string;
    verificationSectionHeader: string;
    passwordSectionHeader: string;
    passwordButtonLabel: string;
    allowPhoneNumberChange: boolean;
    persistVerificationCode: boolean;
    propertySlug?: string;
}

const SetPasswordWorkflow = (props: Props) => {
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [codeIsVerified, setCodeIsVerified] = useState<boolean>(false);
    const [passwordToken, setPasswordToken] = useState<string>('');

    const handleResendCode = async () => {
        props.setIsLoading(true);
        let resendResult = await resendRegistrationText(props.phoneNumber);

        if (resendResult.success) {
            props.setIsLoading(false);
            return true;
        } else {
            props.setErrorMessage(resendResult.error);
            return false;
        }
    };

    const handleRedeem = async () => {
        props.setIsLoading(true);
        let redeemResult = await redeemPhoneCode({
            phoneNumber: props.phoneNumber,
            verificationCode: verificationCode
        });

        if (redeemResult.success && redeemResult.data) {
            setPasswordToken(redeemResult.data);
            setCodeIsVerified(true);
            props.setIsLoading(false);
            return true;
        } else {
            props.setErrorMessage(redeemResult.error);
            props.setIsLoading(false);
            return false;
        }
    };

    const handleLogin = async (password: string) => {
        props.setIsLoading(true);

        let loginResult = await logIn(props.phoneNumber, password);

        if (loginResult.success && loginResult.data) {
            setTokens(loginResult.data.jwt, loginResult.data.refreshToken);
            browserHistory.push(
                props.propertySlug
                    ? tourSignupPath(props.propertySlug)(TourPage.GuestCard)
                    : prospectPath(prospectPages.welcome)
            );
            return true;
        } else {
            props.setIsLoading(false);
            props.setErrorMessage(loginResult.error);
            return false;
        }
    };

    const handlePasswordSubmit = async (password: string) => {
        props.setIsLoading(true);

        let submissionResult = await props.onPasswordSubmit({
            username: props.phoneNumber,
            password: password,
            token: passwordToken
        });

        if (submissionResult.success) {
            handleLogin(password);
            return true;
        } else {
            props.setErrorMessage(submissionResult.error);
            props.setIsLoading(false);
            return false;
        }
    };

    return (
        <section>
            <div>
                {(!codeIsVerified || props.persistVerificationCode) && (
                    <div>
                        <h3 className="prospect-signup-header">
                            {props.verificationSectionHeader}
                        </h3>
                        {props.allowPhoneNumberChange && (
                            <section className="bp-1">
                                <span>{formatPhoneNumber(props.phoneNumber)}</span>
                                {!codeIsVerified && (
                                    <button
                                        className="change-phone-number"
                                        onClick={props.changePhoneNumberEvent}
                                        type="button"
                                    >
                                        Change
                                    </button>
                                )}
                            </section>
                        )}
                        <InputWithValidationIcon
                            labelText={props.verificationCodeInputLabel}
                            boldTitle={true}
                            name="verificationCode"
                            placeholder="Type in code"
                            value={verificationCode}
                            disabled={codeIsVerified}
                            maxLength={6}
                            showIcon={codeIsVerified}
                            validIcon={true}
                            onChange={(_, value) => setVerificationCode(value.toString())}
                        />
                    </div>
                )}

                {codeIsVerified ? (
                    <div>
                        {!props.persistVerificationCode && (
                            <h3 className="prospect-signup-header">
                                {props.passwordSectionHeader}
                            </h3>
                        )}
                        <div className="code-confirmed-label">
                            <label>{props.codeConfirmedLabel}</label>
                        </div>
                        <ProspectNewPasswordEntry
                            labelText="Create a new password"
                            buttonText={props.passwordButtonLabel}
                            onSubmit={handlePasswordSubmit}
                            setErrorMessage={props.setErrorMessage}
                        />
                    </div>
                ) : (
                    <div>
                        <div className="resend-verification-code">
                            <button onClick={handleResendCode}>Resend verification code</button>
                        </div>
                        <div className="prospect-auth-button-container">
                            <Button
                                text="Next"
                                addlClassName="btn-full-width btn-primary"
                                removeBoldFont={true}
                                onClick={() => handleRedeem()}
                                disabled={verificationCode.length < 6}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default SetPasswordWorkflow;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/public/SetPasswordWorkflow.tsx